import type { PDFDocumentProxy } from "pdfjs-dist";
// eslint-disable-next-line import/extensions
import type { EventBus, PDFViewer } from "pdfjs-dist/legacy/web/pdf_viewer.mjs";
import type { PDFViewerOptions } from "pdfjs-dist/types/web/pdf_viewer";
import React, {
    type PointerEventHandler,
    PureComponent,
    type RefObject,
} from "react";
import { type Root, createRoot } from "react-dom/client";
import { debounce } from "ts-debounce";
import type {
    IHighlight,
    LTWH,
    LTWHP,
    Position,
    Scaled,
    ScaledPosition,
    TViewportHighlight,
} from "../types";
import { HighlightLayer, MouseSelection, TipContainer } from "./components";
import { findOrCreateContainerLayer, scaledToViewport, viewportToScaled, getAreaAsPNG, getWindow, isHTMLElement, getPagesFromRange, getClientRects, getBoundingRect, getPageFromElement } from "../lib";
import "./css/style.css";

interface State<THt> {
    ghostHighlight: {
        position: ScaledPosition;
        content?: { text?: string; image?: string };
    } | null;
    isCollapsed: boolean;
    range: Range | null;
    tip: {
        highlight: TViewportHighlight<THt>;
        callback: (highlight: TViewportHighlight<THt>) => JSX.Element;
    } | null;
    tipPosition: Position | null;
    tipChildren: JSX.Element | null;
    isAreaSelectionInProgress: boolean;
    scrolledToHighlightId: string;
}

interface Props<THt> {
    highlightTransform: (
        highlight: TViewportHighlight<THt>,
        index: number,
        setTip: (
            highlight: TViewportHighlight<THt>,
            callback: (highlight: TViewportHighlight<THt>) => JSX.Element,
        ) => void,
        hideTip: () => void,
        viewportToScaled: (rect: LTWHP) => Scaled,
        screenshot: (position: LTWH) => string,
        isScrolledTo: boolean,
    ) => JSX.Element;
    highlights: Array<THt>;
    onScrollChange: () => void;
    scrollRef: (scrollTo: (highlight: THt) => void) => void;
    pdfDocument: PDFDocumentProxy;
    pdfScaleValue: string;
    onSelectionFinished: (
        position: ScaledPosition,
        content: { text?: string; image?: string },
        hideTipAndSelection: () => void,
        transformSelection: () => void,
    ) => JSX.Element | null;
    enableAreaSelection: (event: MouseEvent) => boolean;
    pdfViewerOptions?: PDFViewerOptions;
}

const EMPTY_ID = "empty-id";

export class PdfHighlighter<THt extends IHighlight> extends PureComponent<
    Props<THt>,
    State<THt>
> {
    viewer!: PDFViewer;

    resizeObserver: ResizeObserver | null = null;

    containerNode?: HTMLDivElement | null = null;

    containerNodeRef: RefObject<HTMLDivElement>;

    highlightRoots: {
        [page: number]: { reactRoot: Root; container: Element };
    } = {};

    debouncedAfterSelection: () => void;

    debouncedScaleValue: () => void;

    constructor(props: Props<THt>) {
        super(props);
        this.state = {
            ghostHighlight: null,
            isCollapsed: true,
            range: null,
            scrolledToHighlightId: EMPTY_ID,
            isAreaSelectionInProgress: false,
            tip: null,
            tipPosition: null,
            tipChildren: null,
        };
        this.containerNodeRef = React.createRef();
        this.debouncedAfterSelection = debounce(this.afterSelection, 500);
        this.debouncedScaleValue = debounce(this.handleScaleValue, 500);
        if (typeof ResizeObserver !== "undefined") {
            this.resizeObserver = new ResizeObserver(this.debouncedScaleValue);
        }
    }

    componentDidMount() {
        this.init();
    }

    componentDidUpdate(prevProps: Props<THt>) {
        const { pdfDocument, highlights } = this.props;
        if (prevProps.pdfDocument !== pdfDocument) {
            this.init();
            return;
        }
        if (prevProps.highlights !== highlights) {
            this.renderHighlightLayers();
        }
    }

    componentWillUnmount() {
        this.unsubscribe();
    }

    setTip(position: Position, inner: JSX.Element | null) {
        this.setState({
            tipPosition: position,
            tipChildren: inner,
        });
    }

    scaledPositionToViewport = ({
        pageNumber,
        boundingRect,
        rects,
        usePdfCoordinates,
    }: ScaledPosition): Position => {
        const { viewport } = this.viewer.getPageView(pageNumber - 1);

        return {
            boundingRect: scaledToViewport(boundingRect, viewport, usePdfCoordinates),
            rects: (rects || []).map((rect) =>
                scaledToViewport(rect, viewport, usePdfCoordinates),
            ),
            pageNumber,
        };
    };

    viewportPositionToScaled = ({
        pageNumber,
        boundingRect,
        rects,
    }: Position): ScaledPosition => {
        const { viewport } = this.viewer.getPageView(pageNumber - 1);

        return {
            boundingRect: viewportToScaled(boundingRect, viewport),
            rects: (rects || []).map((rect) => viewportToScaled(rect, viewport)),
            pageNumber,
        };
    };

    screenshot = (position: LTWH, pageNumber: number): string => {
        const { canvas } = this.viewer.getPageView(pageNumber - 1);

        return getAreaAsPNG(canvas, position);
    };

    hideTipAndSelection = () => {
        this.setState({
            tipPosition: null,
            tipChildren: null,
        });

        this.setState({ ghostHighlight: null, tip: null }, () =>
            this.renderHighlightLayers(),
        );
    };

    showTip = (highlight: TViewportHighlight<THt>, content: JSX.Element): void => {
        const { isCollapsed, ghostHighlight, isAreaSelectionInProgress } =
            this.state;

        const highlightInProgress = !isCollapsed || ghostHighlight;

        if (highlightInProgress || isAreaSelectionInProgress) {
            return;
        }

        this.setTip(highlight.position, content);
    };

    // eslint-disable-next-line class-methods-use-this
    unsubscribe = (): void => {
        // This method is reassigned in attachRef to use 'this'
    };

    attachRef = (eventBus: EventBus) => {
        const { resizeObserver: observer } = this;
        this.containerNode = this.containerNodeRef.current;
        this.unsubscribe();

        if (this.containerNode) {
            const { ownerDocument: doc } = this.containerNode;
            eventBus.on("textlayerrendered", this.onTextLayerRendered);
            eventBus.on("pagesinit", this.onDocumentReady);
            doc.addEventListener("selectionchange", this.onSelectionChange);
            doc.addEventListener("keydown", this.handleKeyDown);
            // Delay adding resize listener to avoid triggering during initial render
            setTimeout(() => {
                doc.defaultView?.addEventListener("resize", this.debouncedScaleValue);
            }, 100);
            // Delay ResizeObserver to avoid triggering scale changes during initial render
            setTimeout(() => {
                if (observer && this.containerNode) {
                    observer.observe(this.containerNode);
                }
            }, 100);

            this.unsubscribe = () => {
                eventBus.off("pagesinit", this.onDocumentReady);
                eventBus.off("textlayerrendered", this.onTextLayerRendered);
                doc.removeEventListener("selectionchange", this.onSelectionChange);
                doc.removeEventListener("keydown", this.handleKeyDown);
                doc.defaultView?.removeEventListener(
                    "resize",
                    this.debouncedScaleValue,
                );
                if (observer) observer.disconnect();
            };
        }
    };

    renderTip = () => {
        const { tipPosition, tipChildren } = this.state;
        if (!tipPosition) return null;

        const { boundingRect, pageNumber } = tipPosition;
        const page = {
            node: this.viewer.getPageView((boundingRect.pageNumber || pageNumber) - 1)
                .div,
            pageNumber: boundingRect.pageNumber || pageNumber,
        };

        const pageBoundingClientRect = page.node.getBoundingClientRect();

        const pageBoundingRect = {
            bottom: pageBoundingClientRect.bottom,
            height: pageBoundingClientRect.height,
            left: pageBoundingClientRect.left,
            right: pageBoundingClientRect.right,
            top: pageBoundingClientRect.top,
            width: pageBoundingClientRect.width,
            x: pageBoundingClientRect.x,
            y: pageBoundingClientRect.y,
            pageNumber: page.pageNumber,
        };

        return (
            <TipContainer
                scrollTop={this.viewer.container.scrollTop}
                pageBoundingRect={pageBoundingRect}
                style={{
                    left:
                        page.node.offsetLeft + boundingRect.left + boundingRect.width / 2,
                    top: boundingRect.top + page.node.offsetTop,
                    bottom: boundingRect.top + page.node.offsetTop + boundingRect.height,
                }}
            >
                {tipChildren}
            </TipContainer>
        );
    };

    onTextLayerRendered = () => {
        this.renderHighlightLayers();
    };

    scrollTo = (highlight: THt) => {
        const { pageNumber, boundingRect, usePdfCoordinates } = highlight.position;

        this.viewer.container.removeEventListener("scroll", this.onScroll);

        const pageViewport = this.viewer.getPageView(pageNumber - 1).viewport;

        const scrollMargin = 10;

        this.viewer.scrollPageIntoView({
            pageNumber,
            destArray: [
                null,
                { name: "XYZ" },
                ...pageViewport.convertToPdfPoint(
                    0,
                    scaledToViewport(boundingRect, pageViewport, usePdfCoordinates).top -
                    scrollMargin,
                ),
                0,
            ],
        });

        this.setState(
            {
                scrolledToHighlightId: highlight.id,
            },
            () => this.renderHighlightLayers(),
        );

        // wait for scrolling to finish
        setTimeout(() => {
            this.viewer.container.addEventListener("scroll", this.onScroll);
        }, 100);
    };

    onDocumentReady = () => {
        const { scrollRef, pdfScaleValue } = this.props;

        // Scale is already set in init(), just verify it's correct
        if (this.viewer && this.viewer.currentScaleValue !== pdfScaleValue) {
            this.viewer.currentScaleValue = pdfScaleValue;
        }

        scrollRef(this.scrollTo);

        // Render highlights when document is ready
        this.renderHighlightLayers();
    };

    onSelectionChange = () => {
        const container = this.containerNode;
        if (!container) {
            return;
        }

        const selection = getWindow(container).getSelection();
        if (!selection) {
            return;
        }

        const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : null;

        if (selection.isCollapsed) {
            this.setState({ isCollapsed: true });
            return;
        }

        if (
            !range ||
            !container ||
            !container.contains(range.commonAncestorContainer)
        ) {
            return;
        }

        this.setState({
            isCollapsed: false,
            range,
        });

        this.debouncedAfterSelection();
    };

    onScroll = () => {
        const { onScrollChange } = this.props;

        onScrollChange();

        this.setState(
            {
                scrolledToHighlightId: EMPTY_ID,
            },
            () => this.renderHighlightLayers(),
        );

        this.viewer.container.removeEventListener("scroll", this.onScroll);
    };

    onMouseDown: PointerEventHandler = (event) => {
        if (!(event.target instanceof Element) || !isHTMLElement(event.target)) {
            return;
        }

        if (event.target.closest("#PdfHighlighter__tip-container")) {
            return;
        }

        this.hideTipAndSelection();
    };

    handleKeyDown = (event: KeyboardEvent) => {
        if (event.code === "Escape") {
            this.hideTipAndSelection();
        }
    };

    afterSelection = () => {
        const { onSelectionFinished } = this.props;

        const { isCollapsed, range } = this.state;

        if (!range || isCollapsed) {
            return;
        }

        const pages = getPagesFromRange(range);

        if (!pages || pages.length === 0) {
            return;
        }

        const rects = getClientRects(range, pages);

        if (rects.length === 0) {
            return;
        }

        const boundingRect = getBoundingRect(rects);

        const viewportPosition: Position = {
            boundingRect,
            rects,
            pageNumber: pages[0].number,
        };

        const content = {
            text: range.toString(),
        };
        const scaledPosition = this.viewportPositionToScaled(viewportPosition);

        this.setTip(
            viewportPosition,
            onSelectionFinished(
                scaledPosition,
                content,
                () => this.hideTipAndSelection(),
                () =>
                    this.setState(
                        {
                            ghostHighlight: { position: scaledPosition },
                        },
                        () => this.renderHighlightLayers(),
                    ),
            ),
        );
    };

    handleScaleValue = () => {
        if (this.viewer) {
            const { pdfScaleValue } = this.props;
            this.viewer.currentScaleValue = pdfScaleValue;
            // Re-render highlights after scale change to ensure correct positioning
            setTimeout(() => {
                this.renderHighlightLayers();
            }, 100);
        }
    };

    toggleTextSelection(flag: boolean) {
        if (!this.viewer.viewer) {
            return;
        }
        this.viewer.viewer.classList.toggle('disableSelection', flag);
    }

    groupHighlightsByPage(highlights: Array<THt>): {
        [pageNumber: string]: Array<THt>;
    } {
        const { ghostHighlight } = this.state;

        const allHighlights = [...highlights, ghostHighlight].filter(
            Boolean,
        ) as THt[];

        const pageNumbers = new Set<number>();
        allHighlights.forEach((highlight) => {
            pageNumbers.add(highlight.position.pageNumber);
            highlight.position.rects.forEach((rect) => {
                if (rect.pageNumber) {
                    pageNumbers.add(rect.pageNumber);
                }
            });
        });

        const groupedHighlights: Record<number, THt[]> = {};

        Array.from(pageNumbers).forEach((pageNumber) => {
            groupedHighlights[pageNumber] = groupedHighlights[pageNumber] || [];
            allHighlights.forEach((highlight) => {
                const pageSpecificHighlight = {
                    ...highlight,
                    position: {
                        pageNumber,
                        boundingRect: highlight.position.boundingRect,
                        rects: [],
                        usePdfCoordinates: highlight.position.usePdfCoordinates,
                    } as ScaledPosition,
                };
                let anyRectsOnPage = false;
                highlight.position.rects.forEach((rect) => {
                    if (
                        pageNumber === (rect.pageNumber || highlight.position.pageNumber)
                    ) {
                        pageSpecificHighlight.position.rects.push(rect);
                        anyRectsOnPage = true;
                    }
                });
                if (anyRectsOnPage || pageNumber === highlight.position.pageNumber) {
                    groupedHighlights[pageNumber].push(pageSpecificHighlight);
                }
            });
        });

        return groupedHighlights;
    }

    findOrCreateHighlightLayer(page: number) {
        const { textLayer } = this.viewer.getPageView(page - 1) || {};

        if (!textLayer) {
            return null;
        }

        return findOrCreateContainerLayer(
            textLayer.div,
            'PdfHighlighter__highlight-layer highlightLayer',
            ".PdfHighlighter__highlight-layer",
        );
    }

    async init() {
        const { pdfDocument, pdfViewerOptions, pdfScaleValue } = this.props;
        // eslint-disable-next-line import/extensions
        const pdfjs = await import("pdfjs-dist/web/pdf_viewer.mjs");
        const eventBus = new pdfjs.EventBus();
        const linkService = new pdfjs.PDFLinkService({
            eventBus,
            externalLinkTarget: 2,
        });

        if (!this.containerNodeRef.current) {
            throw new Error("!");
        }

        this.viewer =
            this.viewer ||
            new pdfjs.PDFViewer({
                container: this.containerNodeRef.current,
                eventBus,
                // enhanceTextSelection: true, // deprecated. https://github.com/mozilla/pdf.js/issues/9943#issuecomment-409369485
                textLayerMode: 2,
                removePageBorders: true,
                linkService,
                ...pdfViewerOptions,
            });

        linkService.setDocument(pdfDocument);
        linkService.setViewer(this.viewer);
        this.viewer.setDocument(pdfDocument);

        // Set scale immediately after setDocument to prevent initial zoom jump
        // Must be done before attachRef to avoid ResizeObserver triggering scale changes
        this.viewer.currentScaleValue = pdfScaleValue;

        this.attachRef(eventBus);
    }

    private renderHighlightLayer(root: Root, pageNumber: number) {
        const { highlightTransform, highlights } = this.props;
        const { tip, scrolledToHighlightId } = this.state;
        root.render(
            <HighlightLayer
                highlightsByPage={this.groupHighlightsByPage(highlights)}
                pageNumber={pageNumber.toString()}
                scrolledToHighlightId={scrolledToHighlightId}
                highlightTransform={highlightTransform}
                tip={tip}
                scaledPositionToViewport={this.scaledPositionToViewport}
                hideTipAndSelection={this.hideTipAndSelection}
                viewer={this.viewer}
                screenshot={this.screenshot}
                showTip={this.showTip}
                setTip={(newTip) => {
                    this.setState({ tip: newTip });
                }}
            />,
        );
    }

    private renderHighlightLayers() {
        const { pdfDocument } = this.props;
        let pageNumber = 1;
        while (pageNumber <= pdfDocument.numPages) {
            const highlightRoot = this.highlightRoots[pageNumber];
            /** Need to check if container is still attached to the DOM as PDF.js can unload pages. */
            if (highlightRoot?.container.isConnected) {
                this.renderHighlightLayer(highlightRoot.reactRoot, pageNumber);
            } else {
                const highlightLayer = this.findOrCreateHighlightLayer(pageNumber);
                if (highlightLayer) {
                    const reactRoot = createRoot(highlightLayer);
                    this.highlightRoots[pageNumber] = {
                        reactRoot,
                        container: highlightLayer,
                    };
                    this.renderHighlightLayer(reactRoot, pageNumber);
                }
            }
            pageNumber += 1;
        }
    }

    render() {
        const { onSelectionFinished, enableAreaSelection } = this.props;

        return (
            <div onPointerDown={this.onMouseDown}>
                <div
                    ref={this.containerNodeRef}
                    className="container"
                    onContextMenu={(e) => e.preventDefault()}
                >
                    <div className="pdfViewer" />
                    {this.renderTip()}
                    {typeof enableAreaSelection === "function" ? (
                        <MouseSelection
                            onDragStart={() => this.toggleTextSelection(true)}
                            onDragEnd={() => this.toggleTextSelection(false)}
                            onChange={(isVisible) =>
                                this.setState({ isAreaSelectionInProgress: isVisible })
                            }
                            shouldStart={(event) =>
                                enableAreaSelection(event) &&
                                event.target instanceof Element &&
                                isHTMLElement(event.target) &&
                                Boolean(event.target.closest(".page"))
                            }
                            onSelection={(startTarget, boundingRect, resetSelection) => {
                                const page = getPageFromElement(startTarget);

                                if (!page) {
                                    return;
                                }

                                const pageBoundingRect = {
                                    ...boundingRect,
                                    top: boundingRect.top - page.node.offsetTop,
                                    left: boundingRect.left - page.node.offsetLeft,
                                    pageNumber: page.number,
                                };

                                const viewportPosition = {
                                    boundingRect: pageBoundingRect,
                                    rects: [],
                                    pageNumber: page.number,
                                };

                                const scaledPosition =
                                    this.viewportPositionToScaled(viewportPosition);

                                const image = this.screenshot(
                                    pageBoundingRect,
                                    pageBoundingRect.pageNumber,
                                );

                                this.setTip(
                                    viewportPosition,
                                    onSelectionFinished(
                                        scaledPosition,
                                        { image },
                                        () => this.hideTipAndSelection(),
                                        () => {
                                            this.setState(
                                                {
                                                    ghostHighlight: {
                                                        position: scaledPosition,
                                                        content: { image },
                                                    },
                                                },
                                                () => {
                                                    resetSelection();
                                                    this.renderHighlightLayers();
                                                },
                                            );
                                        },
                                    ),
                                );
                            }}
                        />
                    ) : null}
                </div>
            </div>
        );
    }
}